import { useState, useRef } from "react";
import { ContextMenuControl, IconControl, Loading } from "spark-core-dx/components";
import { useVisualFormContext } from "../../../../contexts/VisualFormContext";
import { useDashboardDomainContext } from "../../../../DashboardDomainProvider";
import { DeleteVisualDialog } from "../../../dialogs/DeleteVisualDialog";
import { CardDesignModeOptions, CardViewModeOptions } from "./CardContextMenuLib";
import { useVisualExportContext } from "../../../../contexts/VisualExportContext";
import { useFullscreen } from "../../../../contexts/FullScreenContext";
import UpdatedInfoControl from "../../../UpdatedInfoControl";

export const CardTileHeader = ({ card, visual, isOverlay = false }) => {
  const {
    dashboardLib,
    isViewMode,
    isFilterDebouncing,
    activeContextMenu,
    handleAssignActiveContextMenu
  } = useDashboardDomainContext();

  const [isLocked,setIsLocked] = useState(!!visual?.IsLocked);
  const { openVisualEdit } = useVisualFormContext();
  const { openExportDialog } = useVisualExportContext();
  const { isFullscreen, openFullscreen, closeFullscreen } = useFullscreen();
 
  // FIX 1: logical && (not bitwise &), so the rule is consistent
  const canOpenContext = (isFullscreen && isOverlay) || (!isFullscreen && !isOverlay);

  const showLoadingIndicator = isFilterDebouncing && !visual?.IsLocked;
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  // Normalize IDs to strings to avoid === failures when types differ
  const visualId = visual?.VisualId != null ? String(visual.VisualId) : "";
  const activeId = activeContextMenu != null ? String(activeContextMenu) : "";

  // Ensure the displayed name starts with an uppercase letter
  const titleText = visual?.Name
    ? `${String(visual.Name).charAt(0).toUpperCase()}${String(visual.Name).slice(1)}`
    : visual?.Name;

  // Also stop propagation on pointer down to avoid early close/focus issues
  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // --- Click vs Drag detection to ensure reliability inside draggable items ---
  const clickStateRef = useRef({ x: 0, y: 0, t: 0 });
  const CLICK_MOVE_PX = 5;
  const CLICK_TIME_MS = 400;

  const handleContextMouseDown = (e) => {
    e.stopPropagation();
    clickStateRef.current = { x: e.clientX || 0, y: e.clientY || 0, t: Date.now() };
  };

  const handleContextMouseUp = (e) => {
    e.stopPropagation();
    const s = clickStateRef.current;
    const dx = Math.abs((e.clientX || 0) - s.x);
    const dy = Math.abs((e.clientY || 0) - s.y);
    const dt = Date.now() - s.t;
    if (dx <= CLICK_MOVE_PX && dy <= CLICK_MOVE_PX && dt <= CLICK_TIME_MS) {
      handleAssignActiveContextMenu(e, visualId);
    }
  };

  return visual && (
    <div className={`card-tile-header`}>
      <div className="card-tile-header-left">
        {visual?.Icon && (
          <IconControl
            className={`card-tile-icon`}
            iconKey={visual.Icon}
            color={"var(--primary-text)"}
          />
        )}
        <div className={`card-tile-title`}>{titleText}</div>
        {visual?.Description && (
          <UpdatedInfoControl>{visual.Description}</UpdatedInfoControl>
        )}
      </div>

      <div className="card-tile-header-right">
        {showLoadingIndicator && (
          <div className="card-tile-loading">
            <Loading type="spinner-2" size="1rem" />
          </div>
        )}

        <IconControl
          className={`card-tile-locked-icon ${(visual?.IsLocked || isLocked) ? 'locked' : ''}`}
          iconKey={"lock"}
          color={"var(--primary-text)"}
          onMouseDown={stop}
        />

        <div className={'card-header-context-container'}    onMouseDown={handleContextMouseDown} onMouseUp={handleContextMouseUp} onContextMenu={e => (e.preventDefault(), e.stopPropagation(), handleAssignActiveContextMenu(e, visualId))} draggable={false} onDragStart={e => e.preventDefault()}>
          <IconControl
            className={`card-tile-context-button`}
            iconKey={"viewmore"}
            color={"var(--primary-text)"}
            onMouseDown={handleContextMouseDown}
            onMouseUp={handleContextMouseUp}
            draggable={false}
            onDragStart={e => e.preventDefault()}
         
          />
          <ContextMenuControl
            openContextMenu={canOpenContext ? activeId === visualId : false}
            setOpenContextMenu={() => handleAssignActiveContextMenu(null, null)}
            contextMenuStyle={{}}
          >
            {isViewMode ? (
              <CardViewModeOptions
                visual={visual}
                removeContextMenu={() => handleAssignActiveContextMenu(null, null)}
                onFullScreen={!isFullscreen ? () => openFullscreen(card) : () => closeFullscreen()}
                onExport={() => { openExportDialog(visual); }}
              />
            ) : (
              <CardDesignModeOptions
                visual={visual}
                removeContextMenu={() => handleAssignActiveContextMenu(null, null)}
                onEdit={openVisualEdit}
                onDelete={() => setIsDeleteOpen(true)}
                onLock={(visual)=>{
                    setIsLocked(!visual.IsLocked);
                    dashboardLib.toggleVisualLock(visual);
                }}
              />
            )}
          </ContextMenuControl>
        </div>
      </div>

      <DeleteVisualDialog
        visual={visual}
        isActive={isDeleteOpen}
        closeDialog={() => setIsDeleteOpen(false)}
      />
    </div>
  );
};
